/**
 * One dropped connection must not break a page.
 *
 * The SDK reports a request that never reached the server as "Network Error"
 * (axios has no response to read). On a phone that happens all the time — a
 * tunnel, a lift, a cell handoff — while the very next attempt succeeds. These
 * helpers retry only that kind of failure: when the server answered with a
 * status (401, 404, 429…) the answer is real, so it is passed straight through
 * and never repeated.
 */

export function isTransientError(error) {
  const status = Number(error?.status || error?.response?.status || 0);
  if (status) return false;
  const message = String(error?.message || error || "");
  return /network error|failed to fetch|load failed|timed out|err_network|err_internet|err_connection|err_empty_response/i.test(message);
}

const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs `make()` and, if the transport failed, tries it once more after a short
 * pause. `make` is a function so each attempt is a fresh request rather than a
 * reused settled promise.
 */
export async function withNetworkRetry(make, attempts = 2, delayMs = 900) {
  let lastError;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await make();
    } catch (error) {
      lastError = error;
      if (!isTransientError(error) || attempt === attempts) throw error;
      await pause(delayMs);
    }
  }
  throw lastError;
}